import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { chiusureApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import VenueBadge from '../components/VenueBadge';
import BarraAssistente from '../components/ai/BarraAssistente';
import { toast } from 'sonner';
import { 
  Plus, 
  History, 
  Download, 
  Users, 
  ChevronRight,
  CalendarDays,
  Loader2,
  Wallet,
  AlertCircle,
  Clock,
  PiggyBank
} from 'lucide-react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

const DashboardPage = () => {
  const navigate = useNavigate();
  const { user, isAdmin, canModifyChiusure, canViewUscite } = useAuth();
  const [recentChiusure, setRecentChiusure] = useState([]);
  const [chiusuraParziale, setChiusuraParziale] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    loadRecentChiusure();
    loadChiusuraParziale();
  }, []);

  const loadRecentChiusure = async () => {
    try {
      const data = await chiusureApi.getAll(0, 5);
      setRecentChiusure(data);
    } catch (error) {
      toast.error('Errore nel caricamento delle chiusure');
    } finally {
      setLoading(false);
    }
  };

  const loadChiusuraParziale = async () => {
    try {
      const data = await chiusureApi.getParzialeOggi();
      setChiusuraParziale(data);
    } catch (error) {
      // Silently ignore - no partial closure today
      setChiusuraParziale(null);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await chiusureApi.exportCsv();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `chiusure_${format(new Date(), 'yyyy-MM-dd')}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Export completato!');
    } catch (error) {
      toast.error('Errore durante l\'export');
    } finally {
      setExporting(false);
    }
  };

  const getDayBadgeClass = (giorno) => {
    const classes = {
      'LUN': 'day-badge-lun',
      'MAR': 'day-badge-mar',
      'MER': 'day-badge-mer',
      'GIO': 'day-badge-gio',
      'VEN': 'day-badge-ven',
      'SAB': 'day-badge-sab',
      'DOM': 'day-badge-dom',
    };
    return classes[giorno] || 'bg-muted text-muted-foreground';
  };

  const formatDate = (dateStr) => {
    try {
      return format(new Date(dateStr), 'd MMMM', { locale: it });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="min-h-screen pb-48">
      {/* Header */}
      <header className="glass-header sticky top-0 z-40 px-4 py-4 border-b border-border">
        <div className="max-w-lg mx-auto flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm text-muted-foreground">Ciao, {user?.nome}</p>
            <h1 className="text-2xl font-bold font-heading tracking-tight">Dashboard</h1>
          </div>
          <VenueBadge />
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 py-6 max-w-lg mx-auto space-y-6">
        {/* Banner Chiusura Parziale */}
        {chiusuraParziale && canModifyChiusure() && (
          <Card 
            className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-300 border-2 cursor-pointer card-lift"
            onClick={() => navigate(`/nuova-chiusura?completa=${chiusuraParziale.id}`)}
            data-testid="complete-partial-banner"
          >
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-amber-100 rounded-full">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-amber-800">Chiusura da completare</h3>
                  <p className="text-sm text-amber-700 mt-1">
                    Hai inserito i dati del pranzo di oggi. Clicca qui per completare la chiusura con i dati della cena.
                  </p>
                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-xs bg-amber-200 text-amber-800 px-2 py-1 rounded-full">
                      Pranzo: €{chiusuraParziale.incasso_totale_pranzo?.toFixed(2) || '0.00'}
                    </span>
                    <span className="text-xs bg-amber-200 text-amber-800 px-2 py-1 rounded-full">
                      {chiusuraParziale.coperti_pranzo || 0} coperti
                    </span>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-amber-600" />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Actions */}
        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
            Azioni Rapide
          </h2>
          
          {canModifyChiusure() && (
            <Button 
              data-testid="new-chiusura-btn"
              onClick={() => navigate('/nuova-chiusura')}
              className="w-full h-16 text-lg font-bold uppercase tracking-wider rounded-xl shadow-lg card-lift"
            >
              <Plus className="w-6 h-6 mr-2" />
              Nuova Chiusura
            </Button>
          )}

          {canViewUscite() && (
            <Button 
              data-testid="new-uscita-btn"
              variant="outline"
              onClick={() => navigate('/uscite')}
              className="w-full h-14 text-base font-semibold rounded-xl shadow-sm card-lift border-2 border-primary/20 hover:border-primary/40 hover:bg-primary/5"
            >
              <Wallet className="w-5 h-5 mr-2" />
              Uscite di Cassa
            </Button>
          )}

          <Button 
            data-testid="bilancio-btn"
            variant="outline"
            onClick={() => navigate('/bilancio')}
            className="w-full h-14 text-base font-semibold rounded-xl shadow-sm card-lift border-2 border-green-200 hover:border-green-400 hover:bg-green-50 text-green-700"
          >
            <PiggyBank className="w-5 h-5 mr-2" />
            Bilancio Mensile
          </Button>

          <div className="grid grid-cols-2 gap-3">
            <Button
              data-testid="history-btn"
              variant="secondary"
              onClick={() => navigate('/chiusure')}
              className="h-14 rounded-xl card-lift"
            >
              <History className="w-5 h-5 mr-2" />
              Storico
            </Button>
            <Button
              data-testid="export-btn"
              variant="secondary"
              onClick={handleExport}
              disabled={exporting}
              className="h-14 rounded-xl card-lift"
            >
              {exporting ? (
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              ) : (
                <Download className="w-5 h-5 mr-2" />
              )}
              Esporta
            </Button>
          </div>

          {isAdmin() && (
            <Button
              data-testid="users-btn"
              variant="outline"
              onClick={() => navigate('/gestione-utenti')}
              className="w-full h-14 rounded-xl card-lift"
            >
              <Users className="w-5 h-5 mr-2" />
              Gestione Utenti
            </Button>
          )}
        </section>

        {/* Recent Closures */}
        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
              Ultime Chiusure
            </h2>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate('/chiusure')}
              className="text-primary"
            >
              Vedi tutte
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>

          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : recentChiusure.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-8 text-center">
                <CalendarDays className="w-12 h-12 text-muted-foreground/50 mb-3" />
                <p className="text-muted-foreground">Nessuna chiusura registrata</p>
                <Button 
                  variant="link" 
                  className="mt-2 text-primary"
                  onClick={() => navigate('/nuova-chiusura')}
                >
                  Inserisci la prima chiusura
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {recentChiusure.map((chiusura, index) => (
                <Card 
                  key={chiusura.id}
                  data-testid={`chiusura-item-${index}`}
                  className="receipt-stub cursor-pointer card-lift animate-slide-up"
                  style={{ animationDelay: `${index * 50}ms` }}
                  onClick={() => navigate(`/chiusure/${chiusura.id}`)}
                >
                  <CardContent className="flex items-center justify-between p-4 pl-6">
                    <div className="flex items-center gap-3">
                      <span className={`day-badge ${getDayBadgeClass(chiusura.giorno_settimana)}`}>
                        {chiusura.giorno_settimana}
                      </span>
                      <div>
                        <p className="font-semibold">
                          {formatDate(chiusura.data_servizio)}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {chiusura.coperti_pranzo + chiusura.coperti_cena} coperti
                        </p>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </section>
      </main>

      <BarraAssistente />
    </div>
  );
};

export default DashboardPage;
